import { formatDate } from "@/lib/utils";
import React, { useState } from "react";

import { useSelector } from "react-redux";
import { PlusCircle, Edit, Trash2, List } from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";

import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ReactSelect } from "@/components/ui/react-select";
import { Badge } from "@/components/ui/badge";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";
import RecordNotFound from "@/core/components/RecordNotFound";

const Designations = () => {
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const queryClient = useQueryClient();

  const [showModal, setShowModal] = useState(false);
  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "",
  });

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  // Fetch Designations using React Query
  const { data: designationData, isLoading: isFetching } = useQuery({
    queryKey: ["designations", currentPage, pageSize, searchText],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.DESIGNATIONS, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: searchText,
        },
      });
      return res.data;
    },
    enabled: hasPermission("employee designations read"),
  });

  const designations = designationData?.data || [];
  const paginationMeta = {
    current_page:
      designationData?.meta?.current_page || designationData?.current_page || 1,
    last_page:
      designationData?.meta?.last_page || designationData?.last_page || 1,
    per_page:
      designationData?.meta?.per_page || designationData?.per_page || 15,
    total:
      designationData?.meta?.total ||
      designationData?.total ||
      designations.length,
  };

  // Save Mutation (Add/Edit)
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (selectedDesignation?.id) {
        return apiClient.put(
          `${API_ENDPOINTS.USERS.DESIGNATIONS}/${selectedDesignation.id}`,
          data
        );
      } else {
        return apiClient.post(API_ENDPOINTS.USERS.DESIGNATIONS, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["designations"] });
      toast.success(
        `Designation ${selectedDesignation ? "updated" : "added"} successfully`,
      );
      setShowModal(false);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to save designation");
    },
  });

  // Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`${API_ENDPOINTS.USERS.DESIGNATIONS}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["designations"] });
      toast.success("Designation deleted successfully");
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to delete designation",
      );
    },
  });

  const handleAdd = () => {
    if (!hasPermission("employee designations create")) {
      toast.error("You do not have permission to create designations");
      return;
    }
    setSelectedDesignation(null);
    setFormData({ name: "", description: "", status: "active" });
    setShowModal(true);
  };

  const handleEdit = (designation) => {
    if (!hasPermission("employee designations update")) {
      toast.error("You do not have permission to update designations");
      return;
    }
    setSelectedDesignation(designation);
    setFormData({
      name: designation.name,
      description: designation.description,
      status: designation.status,
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!hasPermission("employee designations delete")) {
      toast.error("You do not have permission to delete designations");
      return;
    }
    deleteMutation.mutate(id);
  };

  const handleSave = () => {
    if (selectedDesignation && !hasPermission("employee designations update")) {
      toast.error("You do not have permission to update designations");
      return;
    }
    if (
      !selectedDesignation &&
      !hasPermission("employee designations create")
    ) {
      toast.error("You do not have permission to create designations");
      return;
    }
    if (!formData.name) {
      toast.error("Name is required");
      return;
    }
    saveMutation.mutate(formData);
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      sortingFn: "text",
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ getValue }) => getValue() || "-",
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => (
        <Badge variant={getValue() === "active" ? "success" : "destructive"}>
          {getValue() === "active" ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    ...(hasPermission("employee designations update") ||
      hasPermission("employee designations delete")
      ? [
        {
          id: "actions",
          header: "Actions",
          cell: ({ row }) => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {hasPermission("employee designations update") && (
                  <DropdownMenuItem onClick={() => handleEdit(row.original)}>
                    <Edit className="mr-2 h-4 w-4" /> Edit
                  </DropdownMenuItem>
                )}
                {hasPermission("employee designations delete") && (
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => handleDelete(row.original.id)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ),
        },
      ]
      : []),
  ];

  if (!hasPermission("employee designations read")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Designation List"
          subtitle="Manage Your Designations"
        />
        <PageHeaderActions>
          {hasPermission("employee designations create") && (
            <Button onClick={handleAdd}>
              <PlusCircle className="mr-2 h-4 w-4" /> Add New Designation
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search designations..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={designations}
          isLoading={isFetching}
          enableRowSelection={true}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            }
          }}
          pageCount={paginationMeta.last_page}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Designations: {paginationMeta.total || 0}
            </span>
          }
        />
      </TableCard>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {selectedDesignation ? "Edit Designation" : "Add New Designation"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">
                Name<span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">Status</Label>
              <ReactSelect
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
                value={
                  [
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ].find((opt) => opt.value === formData.status) || null
                }
                onChange={(opt) =>
                  setFormData({ ...formData, status: opt ? opt.value : "" })
                }
                isSearchable={false}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending
                ? "Saving..."
                : selectedDesignation
                  ? "Update"
                  : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default Designations;
